/**
 * VideoConfigFlyout — Config flyout content for the video node control bar.
 *
 * Five control groups: Model, Aspect Ratio, Resolution, Duration, Generate Audio.
 * Resolution and Duration rendering are model-dependent via getVideoConstraints().
 * All changes persist immediately via onChange callbacks.
 */

import { memo } from 'react'
import { getVideoConstraints } from './videoConstraints'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VideoConfigFlyoutProps {
  model: string
  onModelChange: (model: string) => void
  aspectRatio: string
  onAspectRatioChange: (ratio: string) => void
  resolution: string
  onResolutionChange: (resolution: string) => void
  duration: number
  onDurationChange: (duration: number) => void
  generateAudio: boolean
  onGenerateAudioChange: (generateAudio: boolean) => void
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODEL_OPTIONS = [
  { label: 'Seedance I2V', value: 'seedance-2.0-image-to-video' },
  { label: 'Seedance R2V', value: 'seedance-2.0-reference-to-video' },
  { label: 'Kling 3.0', value: 'kling-v3-pro' },
  { label: 'Veo 3.1', value: 'veo-3.1' },
] as const

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16'] as const

// ---------------------------------------------------------------------------
// Shared styles
// ---------------------------------------------------------------------------

const LABEL_CLASS = 'text-xxs uppercase tracking-wider text-text-muted block mb-1.5'

function segmentedBtnClass(isSelected: boolean): string {
  return `flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
    isSelected
      ? 'bg-primary/20 text-primary border border-primary/40'
      : 'bg-surface-2 text-text-muted border border-border-subtle hover:text-foreground'
  }`
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const VideoConfigFlyout = memo(function VideoConfigFlyout({
  model,
  onModelChange,
  aspectRatio,
  onAspectRatioChange,
  resolution,
  onResolutionChange,
  duration,
  onDurationChange,
  generateAudio,
  onGenerateAudioChange,
}: VideoConfigFlyoutProps) {
  const constraints = getVideoConstraints(model, resolution)

  return (
    <div className="flex flex-col gap-3" data-testid="video-config-flyout-content">
      {/* Model */}
      <div>
        <label className={LABEL_CLASS}>Model</label>
        <div className="flex gap-1">
          {MODEL_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => onModelChange(opt.value)}
              className={segmentedBtnClass(model === opt.value)}
              data-testid={`model-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Aspect Ratio */}
      <div>
        <label className={LABEL_CLASS}>Aspect Ratio</label>
        <div className="flex gap-1">
          {ASPECT_RATIO_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => onAspectRatioChange(opt)}
              className={segmentedBtnClass(aspectRatio === opt)}
              data-testid={`aspect-ratio-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Resolution (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>Resolution</label>
        {constraints.resolutionOptions ? (
          <div className="flex gap-1">
            {constraints.resolutionOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => onResolutionChange(opt)}
                className={segmentedBtnClass(resolution === opt)}
                data-testid={`resolution-${opt}`}
              >
                {opt === '4k' ? '4K' : opt}
              </button>
            ))}
          </div>
        ) : (
          <span
            className="text-xs text-text-secondary"
            data-testid="resolution-static"
          >
            {constraints.resolutionLabel ?? 'Auto'}
          </span>
        )}
      </div>

      {/* Duration (model-dependent) */}
      <div>
        <label className={LABEL_CLASS}>Duration</label>
        {constraints.durationOptions ? (
          <div className="flex flex-wrap gap-1">
            {constraints.durationOptions.map((d) => (
              <button
                key={d}
                onClick={() => onDurationChange(d)}
                className={segmentedBtnClass(duration === d)}
                data-testid={`duration-${d}s`}
              >
                {d}s
              </button>
            ))}
          </div>
        ) : (
          <span
            className="text-xs text-text-secondary"
            data-testid="duration-static"
          >
            {constraints.durationDefault}s
          </span>
        )}
      </div>

      {/* Generate Audio */}
      <div>
        <label className={LABEL_CLASS}>Generate Audio</label>
        <div className="flex gap-1">
          <button
            onClick={() => onGenerateAudioChange(true)}
            className={segmentedBtnClass(generateAudio === true)}
            data-testid="generate-audio-on"
          >
            On
          </button>
          <button
            onClick={() => onGenerateAudioChange(false)}
            className={segmentedBtnClass(generateAudio === false)}
            data-testid="generate-audio-off"
          >
            Off
          </button>
        </div>
      </div>
    </div>
  )
})

VideoConfigFlyout.displayName = 'VideoConfigFlyout'
